/**
 * Affiliate capture helper (first-touch).
 * ----------------------------------------
 * Reads a partner code from the URL (`?ref=...`) and stashes it in
 * localStorage so it survives the email-confirmation / OAuth round-trip on the
 * signup path — mirroring the pattern in `postAuthRedirect.ts`.
 *
 * First-touch: once a valid code is stored it is NOT overwritten by a later
 * different code. The first partner that brought the user wins.
 *
 * Entries expire after 30 days (attribution window) so a stale code doesn't
 * get attached to an unrelated signup months later.
 */
import {
  AFFILIATE_PARAM,
  isAllowedAffiliate,
  normalizeAffiliateCode,
} from '../config/affiliate';

const KEY = 'cevee:affiliateCode';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface StoredAffiliate {
  code: string;
  ts: number;
}

function readStored(): StoredAffiliate | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredAffiliate>;
    if (!parsed.code || !parsed.ts) return null;
    if (Date.now() - parsed.ts > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    if (!isAllowedAffiliate(parsed.code)) return null;
    return { code: parsed.code, ts: parsed.ts };
  } catch {
    return null;
  }
}

/**
 * Read `?ref=` from the current URL and, if it's a known partner code and no
 * valid code is stored yet, persist it (first-touch). Safe to call on every
 * app load; a no-op when there's no param or a code is already stored.
 */
export function captureAffiliateFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(AFFILIATE_PARAM);
    if (!raw) return;

    const code = normalizeAffiliateCode(raw);
    if (!isAllowedAffiliate(code)) return;

    // First-touch: don't overwrite an existing valid code.
    if (readStored()) return;

    localStorage.setItem(KEY, JSON.stringify({ code, ts: Date.now() }));
  } catch {
    /* private-mode storage failure — ignore */
  }
}

/**
 * Return the stored partner code (if any, and still within the attribution
 * window and allowlist), else null. Does not clear the entry.
 */
export function getAffiliateCode(): string | null {
  return readStored()?.code ?? null;
}

/** Remove any stored affiliate code. */
export function clearAffiliate(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
